import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApolloClient,
  NormalizedCacheObject,
  QueryHookOptions,
  QueryResult,
  useApolloClient,
  useMutation,
  useQuery,
} from '@apollo/client'
import axios from 'axios'
import debounce from 'lodash/debounce'
import { EnvelopeIcon } from '@/atoms/Icons/EnvelopeIcon'
import { WarningCircleIcon } from '@/atoms/Icons/WarningCircleIcon'
import { useToast } from '@/molecules/Toast'
import {
  UpdateUserMutation,
  UpdateUserMutationVariables,
  UpdateUserProfileInput,
  UpdateUserProfileMutation,
  UpdateUserProfileMutationVariables,
  UserFinancialsQuery,
  UserProfileQuery,
} from '@/types/codegen-federation'
import { getAnonymousId } from '@/utils/analytics'
import { getHeadersWithSessionId } from '@/utils/analytics/session-utils'
import { getJwtFromCookie, hasAuthJwtCookie, hasAuthSessionCookie } from '@/utils/auth-utils'
import { getUserLocation, UserLocation } from '@/utils/location'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'
import { getWebClient } from '../ApolloClient'
import {
  GET_USER_FINANCIALS,
  GET_USER_PROFILE_QUERY,
  UPDATE_USER_MUTATION,
  UPDATE_USER_PROFILE_MUTATION,
} from './queries'

export const MAX_CHAR_FIRST_NAME = 50
export const MAX_CHAR_LAST_NAME = 50
export const MAX_CHAR_CITY = 50
export const MAX_CHAR_STATE = 50
const ELLIS_BACKEND_URL = process.env.NEXT_PUBLIC_ELLIS_BACKEND_URL

export interface UserAchievements {
  achievedAt: string
  achievementType: 'PIF' | 'SUBSCRIBE' | 'INVEST'
  iconPath: string
  name: string
  projectSlug: string
}

export interface UserProfile {
  user: HydraUser
}

export interface HydraUser {
  email: string
  uuid: string
  isAdmin: boolean
  groups?: { name: string }[]
  profile: {
    firstName: string
    lastName: string
    image: string
    city: string
    state: string
    country: string
    isChosenInvestorSceneClaimed?: boolean
    isChosenPifferSceneClaimed?: boolean
    isChosenSubscriberSceneClaimed?: boolean
    isHosTicketSceneClaimed?: boolean
  }
  userAchievements: UserAchievements[]
  loading: boolean
}

export interface UserGuildMembership {
  user: {
    isGuildMember: boolean
  }
}

/**
 * @deprecated Use useUser from UserService instead.
 */
export const useUserProfile = (options?: QueryHookOptions): QueryResult<UserProfile> => {
  const client = useApolloClient()
  const skip = !hasAuthJwtCookie()
  return useQuery<UserProfile>(GET_USER_PROFILE_QUERY, { client, skip, errorPolicy: 'all', ...options })
}

/**
 * @deprecated Use useUser from UserService instead.
 */
export const useHydraUser = (): HydraUser => {
  const { data, loading } = useUserProfile()

  return data?.user ? { ...data.user, loading } : ({} as HydraUser)
}

export const useUpdateUserProfile = () => {
  const [mutate, updateUserProfileResponse] = useMutation<
    UpdateUserProfileMutation,
    UpdateUserProfileMutationVariables
  >(UPDATE_USER_PROFILE_MUTATION, {
    errorPolicy: 'all',
  })

  const updateUserProfile = useCallback(
    (input: UpdateUserProfileInput) => {
      mutate({ variables: { input } })
    },
    [mutate],
  )

  return useMemo(() => {
    const updatedUser = updateUserProfileResponse?.data?.updateUserProfile
    return { updateUserProfile, updateUserProfileResponse, updatedUser }
  }, [updateUserProfile, updateUserProfileResponse])
}

export type User = NonNullable<UserProfileQuery>['user']
export type UserGroups = NonNullable<NonNullable<UserProfileQuery>['user']>['groups']

export interface UseUserResult extends QueryResult<UserProfileQuery> {
  isLoggedIn: boolean
  user?: User
  userId?: string | null
  groups: UserGroups
  userLocation?: UserLocation
}
export interface UseUserFinancialsResult extends QueryResult<UserFinancialsQuery> {
  userFinancials?: UserFinancialsQuery['user']
}

const debouncedUserProfileErrorLog = debounce(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (context: any, error: any) => {
    logger().error(
      `An unexpected error occurred when attempting to fetch the user profile!`,
      {
        ...context,
        functionLocation: 'UserService.useUser',
        jwt: getJwtFromCookie(),
        anonymousId: getAnonymousId(),
      },
      error,
    )
  },
  5000,
  { leading: true, trailing: false },
)

export const useUser = (options?: QueryHookOptions): UseUserResult => {
  const client = getWebClient()
  const hasJwtCookie = hasAuthJwtCookie()
  const hasSessionCookie = hasAuthSessionCookie()
  // Don't make the API request if the user is not logged in.
  const skip = !hasJwtCookie

  const result = useFetchUser(client, { skip, errorPolicy: 'all', ...options })

  useEffect(() => {
    if (result.error) {
      debouncedUserProfileErrorLog({ hasJwtCookie, hasSessionCookie }, result.error)
    }
  }, [result, hasJwtCookie, hasSessionCookie])

  return result
}

export const useFetchUser = (
  client: ApolloClient<NormalizedCacheObject>,
  options?: QueryHookOptions,
): UseUserResult => {
  const [userLocation, setUserLocation] = useState<UserLocation | undefined>()

  const result = useQuery<UserProfileQuery>(GET_USER_PROFILE_QUERY, { client, ...options })

  useEffect(() => {
    getUserLocation().then((location) => {
      setUserLocation(location)
    })
  }, [])

  return useMemo(() => {
    const user = result?.data?.user
    return {
      ...result,
      user,
      userLocation,
      isLoggedIn: !!user?.uuid,
      userId: user?.uuid,
      groups: user?.groups,
    }
  }, [result, userLocation])
}

/**
 * Use a debouncer because this hook gets used in many components. A useEffect alone would limit the logging
 * to one log entry per component, but we would rather have one log entry per page load.
 */
const debouncedUserFinancialsErrorLog = debounce(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (context: any, error: any) => {
    logger().error(
      `An unexpected error occurred when attempting to fetch user financial data!`,
      {
        functionLocation: 'UserService.useUserFinancials',
        jwt: getJwtFromCookie(),
        anonymousId: getAnonymousId(),
        hasJwtCookie: hasAuthJwtCookie(),
        hasSessionCookie: hasAuthSessionCookie(),
        ...context,
      },
      error,
    )
  },
  5000,
  { leading: true, trailing: false },
)

export const useUserFinancials = (): UseUserFinancialsResult => {
  const result = useQuery<UserFinancialsQuery>(GET_USER_FINANCIALS, {
    skip: !hasAuthJwtCookie(),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    refetchWritePolicy: 'overwrite',
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (result.error) debouncedUserFinancialsErrorLog({}, result.error)
  }, [result])

  return useMemo(() => {
    return { ...result, userFinancials: result?.data?.user }
  }, [result])
}

export const sendPasswordResetEmail = async (email: string) => {
  const response = await axios.post(
    `${ELLIS_BACKEND_URL}/reset-password-request`,
    { email, client_id: 'angel_web' },
    {
      headers: getHeadersWithSessionId({
        'Content-Type': 'application/json',
        Authorization: `${getJwtFromCookie()}`,
      }),
    },
  )
  logger().info('sendPasswordResetEmail', { response })
  return response.data
}

export const useSendPasswordResetEmail = () => {
  const { showToast } = useToast()
  const { t } = useTranslate('account')

  const onSendPasswordResetEmail = useCallback(
    async (email?: string) => {
      if (email) {
        try {
          await sendPasswordResetEmail(email)
          showToast(t('passwordResetEmailSent', 'Please check your email for a link to reset your password.'), {
            icon: React.createElement(EnvelopeIcon, { color: 'white' }),
            showInMillis: 5000,
          })
        } catch (error) {
          showToast(t('passwordResetEmailError', 'There was an error requesting a password reset. Please try again.'), {
            icon: React.createElement(WarningCircleIcon, { color: 'white' }),
            showInMillis: 10000,
          })
        }
      }
    },
    [showToast, t],
  )

  return { onSendPasswordResetEmail }
}

export const useUpdateUser = (options?: { onCompleted?: (data: UpdateUserMutation) => void }) => {
  const [mutate, updateUserResponse] = useMutation<UpdateUserMutation, UpdateUserMutationVariables>(
    UPDATE_USER_MUTATION,
    {
      onCompleted: options?.onCompleted,
    },
  )

  return useMemo(() => {
    return { mutate, updateUserResponse }
  }, [mutate, updateUserResponse])
}
